/**
 * User Analytics View Component
 *
 * Displays user activity analytics with summary stats, user list, and detail views
 */

import { useState, useEffect } from 'react'
import { analyticsAPI } from '../services/api'
import { Card, CardContent, CardHeader, CardTitle } from './ui/card'
import { Button } from './ui/button'
import { Badge } from './ui/badge'
import { roleLabel } from '../lib/roles'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog'

// Format duration from milliseconds to readable string
function formatDuration(ms) {
  if (!ms || ms <= 0) return '0s'
  const seconds = Math.floor(ms / 1000)
  if (seconds < 60) return `${seconds}s`
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m ${seconds % 60}s`
  const hours = Math.floor(minutes / 60)
  return `${hours}h ${minutes % 60}m`
}

// Format relative time
function formatRelativeTime(dateString) {
  if (!dateString) return 'Never'
  const date = new Date(dateString)
  const now = new Date()
  const diffMs = now - date
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMins < 1) return 'Just now'
  if (diffMins < 60) return `${diffMins}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  if (diffDays < 7) return `${diffDays}d ago`

  return date.toLocaleDateString()
}

// Sparkline component - shows 7-day activity trend
function Sparkline({ data = [], width = 80, height = 24, color = '#3b82f6' }) {
  if (!data || data.length === 0 || data.every(d => d === 0)) {
    return (
      <div
        className="flex items-center justify-center text-xs text-muted-foreground"
        style={{ width, height }}
      >
        No activity
      </div>
    )
  }

  const max = Math.max(...data, 1) // Ensure max is at least 1 to avoid division by zero
  const min = 0
  const range = max - min

  // Calculate points for the polyline
  const points = data.map((value, index) => {
    const x = (index / (data.length - 1)) * width
    const y = height - ((value - min) / range) * (height - 4) - 2 // 2px padding top/bottom
    return `${x},${y}`
  }).join(' ')

  // Calculate points for the filled area
  const areaPoints = [
    `0,${height}`,
    ...data.map((value, index) => {
      const x = (index / (data.length - 1)) * width
      const y = height - ((value - min) / range) * (height - 4) - 2
      return `${x},${y}`
    }),
    `${width},${height}`
  ].join(' ')

  const total = data.reduce((a, b) => a + b, 0)

  return (
    <div className="relative group" title={`Last 7 days: ${total} events`}>
      <svg width={width} height={height} className="overflow-visible">
        {/* Filled area under the line */}
        <polygon
          points={areaPoints}
          fill={color}
          fillOpacity="0.1"
        />
        {/* The line */}
        <polyline
          points={points}
          fill="none"
          stroke={color}
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        {/* Dots at each point */}
        {data.map((value, index) => {
          const x = (index / (data.length - 1)) * width
          const y = height - ((value - min) / range) * (height - 4) - 2
          return (
            <circle
              key={index}
              cx={x}
              cy={y}
              r="2"
              fill={color}
              className="opacity-0 group-hover:opacity-100 transition-opacity"
            />
          )
        })}
      </svg>
      {/* Tooltip showing total */}
      <div className="absolute -top-6 left-1/2 -translate-x-1/2 bg-foreground text-background text-xs px-1.5 py-0.5 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
        {total} events
      </div>
    </div>
  )
}

// Summary card component
function SummaryCard({ title, value, subtitle, icon }) {
  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-muted-foreground">{title}</p>
            <p className="text-2xl font-bold">{value}</p>
            {subtitle && (
              <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>
            )}
          </div>
          {icon && <span className="text-3xl opacity-50">{icon}</span>}
        </div>
      </CardContent>
    </Card>
  )
}

// User detail modal
function UserDetailModal({ userId, user, isOpen, onClose }) {
  const [detail, setDetail] = useState(null)
  const [sessions, setSessions] = useState([])
  const [loading, setLoading] = useState(false)
  const [activeTab, setActiveTab] = useState('overview')

  useEffect(() => {
    if (isOpen && userId) {
      fetchDetail()
      fetchSessions()
    }
  }, [isOpen, userId])

  const fetchDetail = async () => {
    setLoading(true)
    try {
      const response = await analyticsAPI.getUserDetail(userId)
      if (response.data.success) {
        setDetail(response.data.data)
      }
    } catch (error) {
      console.error('Failed to fetch user detail:', error)
    } finally {
      setLoading(false)
    }
  }

  const fetchSessions = async () => {
    try {
      const response = await analyticsAPI.getUserSessions(userId, { limit: 10 })
      if (response.data.success) {
        setSessions(response.data.data || [])
      }
    } catch (error) {
      console.error('Failed to fetch sessions:', error)
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {user?.firstName} {user?.lastName} - Activity Detail
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <svg className="animate-spin h-8 w-8 text-primary" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          </div>
        ) : detail ? (
          <div className="space-y-6">
            {/* Tab buttons */}
            <div className="flex space-x-2 border-b">
              <button
                onClick={() => setActiveTab('overview')}
                className={`px-4 py-2 border-b-2 ${activeTab === 'overview' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground'}`}
              >
                Overview
              </button>
              <button
                onClick={() => setActiveTab('sessions')}
                className={`px-4 py-2 border-b-2 ${activeTab === 'sessions' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground'}`}
              >
                Sessions
              </button>
              <button
                onClick={() => setActiveTab('activity')}
                className={`px-4 py-2 border-b-2 ${activeTab === 'activity' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground'}`}
              >
                Recent Activity
              </button>
            </div>

            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="p-4 bg-muted rounded-lg">
                  <p className="text-sm text-muted-foreground">Total Events</p>
                  <p className="text-xl font-bold">{detail.summary?.totalEvents || 0}</p>
                </div>
                <div className="p-4 bg-muted rounded-lg">
                  <p className="text-sm text-muted-foreground">Page Views</p>
                  <p className="text-xl font-bold">{detail.summary?.pageViews || 0}</p>
                </div>
                <div className="p-4 bg-muted rounded-lg">
                  <p className="text-sm text-muted-foreground">Sessions</p>
                  <p className="text-xl font-bold">{detail.summary?.sessionCount || 0}</p>
                </div>
                <div className="p-4 bg-muted rounded-lg">
                  <p className="text-sm text-muted-foreground">Avg Page Time</p>
                  <p className="text-xl font-bold">{formatDuration(detail.summary?.avgPageDuration)}</p>
                </div>
                <div className="p-4 bg-muted rounded-lg col-span-2">
                  <p className="text-sm text-muted-foreground">First Activity</p>
                  <p className="text-lg font-medium">
                    {detail.summary?.firstActivity
                      ? new Date(detail.summary.firstActivity).toLocaleString()
                      : 'N/A'}
                  </p>
                </div>
                <div className="p-4 bg-muted rounded-lg col-span-2">
                  <p className="text-sm text-muted-foreground">Last Activity</p>
                  <p className="text-lg font-medium">
                    {detail.summary?.lastActivity
                      ? new Date(detail.summary.lastActivity).toLocaleString()
                      : 'N/A'}
                  </p>
                </div>
              </div>
            )}

            {/* Sessions Tab */}
            {activeTab === 'sessions' && (
              <div className="space-y-3">
                {sessions.length === 0 ? (
                  <p className="text-center text-muted-foreground py-4">No sessions found</p>
                ) : (
                  sessions.map((session, idx) => (
                    <div key={session.sessionId || idx} className="p-4 border rounded-lg">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <p className="font-medium">
                            {new Date(session.startTime).toLocaleDateString()} at{' '}
                            {new Date(session.startTime).toLocaleTimeString()}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            Duration: {formatDuration(session.duration)}
                          </p>
                        </div>
                        <div className="text-right">
                          <Badge variant="secondary">
                            {session.device?.browser || 'Unknown'} / {session.device?.os || 'Unknown'}
                          </Badge>
                        </div>
                      </div>
                      <div className="flex gap-4 text-sm text-muted-foreground">
                        <span>{session.pageViews || 0} pages</span>
                        <span>{session.actions || 0} actions</span>
                        {session.ipAddress && <span>IP: {session.ipAddress}</span>}
                      </div>
                      {session.pages && session.pages.length > 0 && (
                        <div className="mt-2 text-xs text-muted-foreground">
                          Pages: {session.pages.slice(0, 5).join(' → ')}
                          {session.pages.length > 5 && ` +${session.pages.length - 5} more`}
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            )}

            {/* Activity Tab */}
            {activeTab === 'activity' && (
              <div className="space-y-2">
                {detail.activity?.length === 0 ? (
                  <p className="text-center text-muted-foreground py-4">No recent activity</p>
                ) : (
                  detail.activity?.slice(0, 20).map((event, idx) => (
                    <div key={event._id || idx} className="flex items-center justify-between p-3 border rounded">
                      <div className="flex items-center gap-3">
                        <Badge variant={event.eventType === 'page_view' ? 'default' : 'secondary'}>
                          {event.eventType}
                        </Badge>
                        <span className="text-sm">
                          {event.page?.path || event.action?.type || '-'}
                        </span>
                      </div>
                      <span className="text-xs text-muted-foreground">
                        {formatRelativeTime(event.timestamp)}
                      </span>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        ) : (
          <p className="text-center text-muted-foreground py-8">No data available</p>
        )}
      </DialogContent>
    </Dialog>
  )
}

export default function UserAnalyticsView() {
  const [summary, setSummary] = useState(null)
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(true)
  const [dateRange, setDateRange] = useState('30') // days
  const [selectedUser, setSelectedUser] = useState(null)

  useEffect(() => {
    fetchData()
  }, [dateRange])

  const fetchData = async () => {
    setLoading(true)
    try {
      const endDate = new Date()
      const startDate = new Date()
      startDate.setDate(startDate.getDate() - parseInt(dateRange))

      const [summaryRes, usersRes] = await Promise.all([
        analyticsAPI.getSummary({
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString()
        }),
        analyticsAPI.getUsers({
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString()
        })
      ])

      if (summaryRes.data.success) {
        setSummary(summaryRes.data.data)
      }

      if (usersRes.data.success) {
        setUsers(usersRes.data.data || [])
      }
    } catch (error) {
      console.error('Failed to fetch analytics:', error)
    } finally {
      setLoading(false)
    }
  }

  const getRoleBadgeColor = (role) => {
    const colors = {
      super_admin: 'bg-purple-100 text-purple-800',
      clinic_admin: 'bg-blue-100 text-blue-800',
      physician_manager: 'bg-green-100 text-green-800',
      pharmacy_manager: 'bg-yellow-100 text-yellow-800',
      program_manager: 'bg-indigo-100 text-indigo-800',
      data_entry: 'bg-gray-100 text-gray-800',
      viewer: 'bg-slate-100 text-slate-800',
      report_viewer: 'bg-cyan-100 text-cyan-800'
    }
    return colors[role] || 'bg-gray-100 text-gray-800'
  }

  return (
    <div className="space-y-6">
      {/* Date Range Filter */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">User Activity Analytics</h2>
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">Time Range:</span>
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            className="px-3 py-1.5 border rounded-md text-sm bg-background"
          >
            <option value="7">Last 7 days</option>
            <option value="30">Last 30 days</option>
            <option value="90">Last 90 days</option>
          </select>
          <Button variant="outline" size="sm" onClick={fetchData}>
            Refresh
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      {loading ? (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {[...Array(5)].map((_, i) => (
            <Card key={i}>
              <CardContent className="pt-6">
                <div className="animate-pulse">
                  <div className="h-4 bg-muted rounded w-1/2 mb-2"></div>
                  <div className="h-8 bg-muted rounded w-3/4"></div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : summary ? (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <SummaryCard
            title="Unique Users"
            value={summary.uniqueUsers || 0}
            icon="👥"
          />
          <SummaryCard
            title="Total Sessions"
            value={summary.totalSessions || 0}
            icon="🔄"
          />
          <SummaryCard
            title="Page Views"
            value={summary.pageViews || 0}
            icon="📄"
          />
          <SummaryCard
            title="Total Events"
            value={summary.totalEvents || 0}
            icon="📊"
          />
          <SummaryCard
            title="Avg Session"
            value={formatDuration(summary.avgSessionDuration)}
            icon="⏱️"
          />
        </div>
      ) : null}

      {/* Device & Browser Breakdown */}
      {!loading && summary && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Device Breakdown */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Device Types</CardTitle>
            </CardHeader>
            <CardContent>
              {Object.entries(summary.deviceBreakdown || {}).length === 0 ? (
                <p className="text-sm text-muted-foreground">No data</p>
              ) : (
                <div className="space-y-2">
                  {Object.entries(summary.deviceBreakdown || {}).map(([device, count]) => (
                    <div key={device} className="flex justify-between items-center">
                      <span className="text-sm capitalize">{device}</span>
                      <Badge variant="secondary">{count}</Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Browser Breakdown */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Browsers</CardTitle>
            </CardHeader>
            <CardContent>
              {Object.entries(summary.browserBreakdown || {}).length === 0 ? (
                <p className="text-sm text-muted-foreground">No data</p>
              ) : (
                <div className="space-y-2">
                  {Object.entries(summary.browserBreakdown || {}).map(([browser, count]) => (
                    <div key={browser} className="flex justify-between items-center">
                      <span className="text-sm">{browser}</span>
                      <Badge variant="secondary">{count}</Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Top Pages */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Top Pages</CardTitle>
            </CardHeader>
            <CardContent>
              {(summary.topPages || []).length === 0 ? (
                <p className="text-sm text-muted-foreground">No data</p>
              ) : (
                <div className="space-y-2">
                  {(summary.topPages || []).slice(0, 5).map((page, idx) => (
                    <div key={idx} className="flex justify-between items-center">
                      <span className="text-sm truncate max-w-[150px]" title={page.path}>
                        {page.path}
                      </span>
                      <Badge variant="secondary">{page.views}</Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* User Analytics Table */}
      <Card>
        <CardHeader>
          <CardTitle>User Activity</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left p-4 font-medium text-muted-foreground">User</th>
                  <th className="text-left p-4 font-medium text-muted-foreground">Role</th>
                  <th className="text-center p-4 font-medium text-muted-foreground">7-Day Trend</th>
                  <th className="text-right p-4 font-medium text-muted-foreground">Sessions</th>
                  <th className="text-right p-4 font-medium text-muted-foreground">Page Views</th>
                  <th className="text-right p-4 font-medium text-muted-foreground">Avg Duration</th>
                  <th className="text-left p-4 font-medium text-muted-foreground">Last Active</th>
                  <th className="text-left p-4 font-medium text-muted-foreground">Devices</th>
                  <th className="text-left p-4 font-medium text-muted-foreground">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="9" className="text-center py-8">
                      <div className="flex items-center justify-center">
                        <svg className="animate-spin h-8 w-8 text-primary" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span className="ml-3 text-muted-foreground">Loading...</span>
                      </div>
                    </td>
                  </tr>
                ) : users.length === 0 ? (
                  <tr>
                    <td colSpan="9" className="text-center py-8 text-muted-foreground">
                      <p>No user activity data found</p>
                      <p className="text-sm">Activity will appear here once users start using the app</p>
                    </td>
                  </tr>
                ) : (
                  users.map((userAnalytics) => (
                    <tr key={userAnalytics.userId} className="border-b border-border last:border-0 hover:bg-muted/50">
                      <td className="p-4">
                        {userAnalytics.user ? (
                          <div>
                            <div className="font-medium">
                              {userAnalytics.user.firstName} {userAnalytics.user.lastName}
                            </div>
                            <div className="text-sm text-muted-foreground">
                              {userAnalytics.user.email}
                            </div>
                          </div>
                        ) : (
                          <span className="text-muted-foreground">Unknown User</span>
                        )}
                      </td>
                      <td className="p-4">
                        {userAnalytics.user?.role && (
                          <Badge className={getRoleBadgeColor(userAnalytics.user.role)}>
                            {roleLabel(userAnalytics.user.role)}
                          </Badge>
                        )}
                      </td>
                      <td className="p-4">
                        <div className="flex justify-center">
                          <Sparkline data={userAnalytics.sparklineData} />
                        </div>
                      </td>
                      <td className="p-4 text-right font-medium">
                        {userAnalytics.sessionCount || 0}
                      </td>
                      <td className="p-4 text-right font-medium">
                        {userAnalytics.pageViews || 0}
                      </td>
                      <td className="p-4 text-right">
                        {formatDuration(userAnalytics.avgSessionDuration)}
                      </td>
                      <td className="p-4 text-sm text-muted-foreground">
                        {formatRelativeTime(userAnalytics.lastActivity)}
                      </td>
                      <td className="p-4">
                        <div className="flex gap-1">
                          {(userAnalytics.devices || []).map((device, idx) => (
                            <Badge key={idx} variant="outline" className="text-xs">
                              {device}
                            </Badge>
                          ))}
                        </div>
                      </td>
                      <td className="p-4">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setSelectedUser(userAnalytics)}
                        >
                          View
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* User Detail Modal */}
      <UserDetailModal
        userId={selectedUser?.userId}
        user={selectedUser?.user}
        isOpen={!!selectedUser}
        onClose={() => setSelectedUser(null)}
      />
    </div>
  )
}
